// EditGradeModal.jsx
import React, { useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import { updateGrade } from "../../../services/gradCnServices";
import { getAdherentById } from "../../../services/adherentServices";
import { toast } from "react-toastify";

const defaultAvatar = "/img/user_avatar.jpg";

const EditGradeModal = ({ isOpen, onClose, grade, refreshList }) => {
  const [formValues, setFormValues] = useState({
    adherent_id: "",
    dominante: "",
    uv1: false,
    uv2: false,
    uv3_points: "",
    uv3: false,
    uv4: false,
    observations: "",
  });

  const [adherent, setAdherent] = useState(null);
  const [loadingAdherent, setLoadingAdherent] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate form + fetch adherent details
  useEffect(() => {
    if (!isOpen || !grade) return;

    // hydrate form from grade
    setFormValues({
      adherent_id: grade.adherent_id,
      dominante: grade.dominante || "",
      uv1: grade.uv1 === 1 || grade.uv1 === true,
      uv2: grade.uv2 === 1 || grade.uv2 === true,
      uv3_points: grade.uv3_points ?? "",
      uv3: grade.uv3 === 1 || grade.uv3 === true,
      uv4: grade.uv4 === 1 || grade.uv4 === true,
      observations: grade.observations || "",
    });

    // fetch adherent identity
    (async () => {
      try {
        setLoadingAdherent(true);
        const data = await getAdherentById(grade.adherent_id);
        setAdherent(data || null);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement de l'adhérent.");
        setAdherent(null);
      } finally {
        setLoadingAdherent(false);
      }
    })();
  }, [isOpen, grade]);

  // ESC pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    onClose?.();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      await updateGrade(grade.id, {
        ...formValues,
        uv3_points:
          formValues.uv3_points === "" ? 0 : Number(formValues.uv3_points),
      });
      toast.success("Grade mis à jour avec succès.");
      await Promise.resolve(refreshList?.());
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du grade", error);
      toast.error("Erreur lors de la mise à jour du grade.");
    } finally {
      setSaving(false);
    }
  };

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] font-montserrat">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-[720px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 flex flex-col max-h-[85vh] overflow-hidden">
          {/* Header dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between rounded-t-2xl">
            <h3 className="text-white font-semibold">Éditer un Grade CN</h3>
            <Button
              text="Fermer"
              icon={FaTimes}
              onClick={handleClose}
              className="!rounded-xl bg-white/10 hover:bg-white/20 text-white gap-2"
            />
          </div>

          {/* Body */}
          <div className="p-5 overflow-y-auto">
            {/* Carte identité adhérent (à la place du select) */}
            <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/40 p-4 sm:p-5 mb-4">
              <div className="flex items-center gap-4">
                <img
                  src={toAbsolute(adherent?.photo_url) || toAbsolute(adherent?.photo) || defaultAvatar}
                  onError={(e) => (e.currentTarget.src = defaultAvatar)}
                  alt={`${adherent?.prenom || ""} ${adherent?.nom || ""}`}
                  className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover ring-2 ring-white dark:ring-slate-800 shadow"
                />
                <div className="min-w-0">
                  <h4 className="text-base sm:text-lg font-semibold truncate">
                    {loadingAdherent
                      ? "Chargement…"
                      : adherent
                      ? `${adherent.nom} ${adherent.prenom}`
                      : "—"}
                  </h4>
                  {/* <p className="text-xs opacity-70 mt-1">
                    ID adhérent : {grade?.adherent_id ?? "—"}
                  </p> */}
                </div>
              </div>
            </div>

            {/* Formulaire d'édition */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Dominante */}
              <div>
                <label className="block text-xs mb-1 opacity-70">
                  Dominante
                </label>
                <select
                  name="dominante"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                  value={formValues.dominante}
                  onChange={handleChange}
                  required
                >
                  <option value="">Sélectionnez une dominante</option>
                  <option value="Compétition">Compétition</option>
                  <option value="Technique">Technique</option>
                </select>
              </div>

              {/* UVs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv1"
                    checked={formValues.uv1}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV1</span>
                </label>

                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv2"
                    checked={formValues.uv2}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV2</span>
                </label>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs mb-1 opacity-70">
                    UV3 Points
                  </label>
                  <input
                    type="number"
                    name="uv3_points"
                    placeholder="0"
                    min="0"
                    value={formValues.uv3_points}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                    required
                  />
                </div>

                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv3"
                    checked={formValues.uv3}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV3</span>
                </label>

                <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                  <input
                    type="checkbox"
                    name="uv4"
                    checked={formValues.uv4}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                  />
                  <span>UV4</span>
                </label>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs mb-1 opacity-70">
                    Observations
                  </label>
                  <textarea
                    name="observations"
                    value={formValues.observations}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-center gap-2 pt-2">
                <Button
                  text={saving ? "Enregistrement..." : "Enregistrer"}
                  icon={FaSave}
                  type="submit"
                  disabled={saving}
                  className="!rounded-xl gap-2"
                />
                <Button
                  text="Annuler"
                  icon={FaTimes}
                  onClick={handleClose}
                  type="button"
                  className="!rounded-xl !bg-slate-100 !text-slate-900 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditGradeModal;
